import moment from "moment";
import { useReducer } from "react";
import SkipReasonContext, { internalState } from "./context";
import reducer from "./reducer";
import { execute } from "../../utils/helpers/execute";
import EndPoints from "../../services/end-points";
import { ISkipReason } from "../../models/skip-reason/response";
import {
  ICreateSkipReason,
  IUpdateSkipReason,
} from "../../models/skip-reason/request";
import { ISkipReasonQuery } from "../../models/skip-reason/query";

/**
 * Map API response (snake_case) to model (camelCase)
 * API returns: ar_name, en_name, is_store_skip, is_active
 * Model expects: arName, enName, isStoreSkip, isActive
 */
const mapSkipReasonResponse = (item: any): ISkipReason => ({
  id: item.id,
  code: item.code,
  arName: item.ar_name ?? item.arName,
  enName: item.en_name ?? item.enName,
  isStoreSkip: item.is_store_skip ?? item.isStoreSkip ?? false,
  isActive: item.is_active ?? item.isActive ?? true,
  createdAt: item.created_at ?? item.createdAt,
  updatedAt: item.updated_at ?? item.updatedAt,
  createdBy: item.created_by ?? item.createdBy,
  updatedBy: item.updated_by ?? item.updatedBy,
  deletedAt: item.deleted_at ?? item.deletedAt,
  deletedBy: item.deleted_by ?? item.deletedBy,
});

export interface IProps {
  children: React.ReactNode;
}

const SkipReasonContextProvider: React.FC<IProps> = (props) => {
  const [state, dispatch] = useReducer(reducer, internalState);

  const getData = async () => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });

        const { data } = await EndPoints.skipReason.getAllSkipReasons(
          state.query
        );

        // Map snake_case API response to camelCase model
        const mappedData = {
          ...data,
          data: data.data?.map(mapSkipReasonResponse) ?? [],
        };

        dispatch({ type: "SET_LIST", payload: { list: mappedData } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "list" } });
      },
      throwException: false,
    });
  };

  const getDetails = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });

        const { data: response } = await EndPoints.skipReason.getSkipReason(id);
        // API returns { success: true, data: {...} }, extract the actual data
        const skipReasonData = (response as any)?.data ?? response;
        // Map snake_case API response to camelCase model
        const mappedDetails = mapSkipReasonResponse(skipReasonData);
        dispatch({ type: "SET_DETAILS", payload: { details: mappedDetails } });
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({ type: "LOADING", payload: { loading: "details" } });
      },
      throwException: false,
    });
  };

  const setDetails = async (data?: ISkipReason) => {
    dispatch({ type: "SET_DETAILS", payload: { details: data } });
  };

  const createSkipReason = async (request: ICreateSkipReason) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });

        await EndPoints.skipReason.createSkipReason(request);
        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
      },
      throwException: true,
    });
  };

  const updateSkipReason = async (id: number, request: IUpdateSkipReason) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "update" },
        });
        await EndPoints.skipReason.updateSkipReason(id, request);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "update" },
        });
      },
      throwException: true,
    });
  };

  const deleteSkipReason = async (id: number) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "delete" },
        });

        await EndPoints.skipReason.deleteSkipReason(id);

        getData();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "delete" },
        });
      },
      throwException: true,
    });
  };

  const setSearch = (search?: string) => {
    dispatch({ type: "SET_SEARCH", payload: { search } });
  };

  const setQuery = (query: ISkipReasonQuery) => {
    dispatch({ type: "SET_QUERY", payload: { query } });
  };

  const exportExcel = async (request: any) => {
    await execute({
      callback: async () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
        const data = await EndPoints.skipReason.exportExcel(
          request,
          state.query
        );
        const url = window.URL.createObjectURL(new Blob([data.data]));
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute(
          "download",
          `SkipReasons-${moment().format("yyyy-MM-d")}.xlsx`
        );
        document.body.appendChild(link);
        link.click();
      },
      fallback: (error) => {},
      finallyCallback: () => {
        dispatch({
          type: "LOADING",
          payload: { loading: "create" },
        });
      },
      throwException: true,
    });
  };

  return (
    <SkipReasonContext.Provider
      value={{
        ...state,
        actions: {
          getData,
          getDetails,
          setDetails,

          createSkipReason,
          updateSkipReason,
          deleteSkipReason,
          exportExcel,

          setSearch,
          setQuery,
        },
      }}
    >
      {props.children}
    </SkipReasonContext.Provider>
  );
};

export default SkipReasonContextProvider;
